import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';

import { ComponentRegistry } from 'core/index';

import FormActionButton from 'libs/forms/views/formActionButton';
import { Divider, ProTip } from 'apps/components/shared';
import { Banner, Paragraph } from 'components';
import { Dropdown } from 'apps/autocomplete/views/dropdown';
import { SocialButtons } from 'apps/signIn/views/socialButtons';
import { Spacer } from 'styles/utils';

import { Message } from 'legacyComponents/styled/lib';
import {
  Main,
  FormWrapper,
  FormField,
  FieldsWrapper,
  Label,
  Optional,
  HeadingStyled,
  FormButtonWrapper,
  EmptyWrapper,
  EmptyLabelWrapper,
} from 'libs/forms/styles';

export const FullFormField = withNamespaces()(
  observer(
    ({
      fieldStore,
      refs,
      responsive = false,
      optionHandler = () => {},
      visible,
    }) => {
      const fieldConf = fieldStore.conf;
      const { formStore } = fieldStore;
      const { conf } = formStore;
      const { styles = {} } = conf;
      const { style = {} } = fieldConf;
      const { formColumns = 1, labelBold = false } = styles;
      // Remove EmptyWrapper of remove fieldStore
      if (fieldConf?.emptySpace && fieldConf?.key)
        return <EmptyWrapper key={fieldConf.key} />;

      const FieldComponent =
        ComponentRegistry.get(fieldConf.component) ||
        ComponentRegistry.get('forms.TextField');

      let fieldLabel = '';
      if (fieldConf.shouldRenderFieldLabel !== false) {
        fieldLabel =
          fieldConf.label && !fieldConf.delegateLabel ? (
            <Label bold={labelBold || fieldConf.boldLabel}>
              <Trans>{fieldConf.label}</Trans>
              {fieldConf?.component === 'forms.CheckboxField'
                ? null
                : !!fieldConf.optional &&
                  !formStore.conf.validIf &&
                  !fieldStore.nullLabel && (
                    <Optional>
                      &nbsp;- <Trans>optional</Trans>
                    </Optional>
                  )}
            </Label>
          ) : (
            fieldConf?.emptyLabel && <EmptyLabelWrapper />
          );
      }

      if (visible && visible() === false) {
        return null;
      }
      return (
        <FormField
          key={fieldStore.fieldName}
          gridColumn={fieldConf.oneColumn && formColumns + 1}
          formColumns={formColumns}
          style={style}
        >
          {fieldConf.fieldLabel !== false && fieldLabel}
          <FieldComponent
            paragraphSpacing={fieldConf.paragraphSpacing}
            fieldStore={fieldStore}
            optionHandler={optionHandler}
            ref={input => {
              // eslint-disable-next-line no-param-reassign
              refs[fieldStore.fieldName] = input;
            }}
            noValidationIcon={!!fieldConf.noValidationIcon}
            responsive={responsive}
          />
          {!!fieldStore.optionsManager && (
            <Dropdown
              inputFields={refs}
              fieldName={fieldStore.fieldName}
              manager={fieldStore.optionsManager}
            />
          )}
        </FormField>
      );
    },
  ),
);

FullFormField.propTypes = {
  fieldStore: PropTypes.object,
  refs: PropTypes.object,
  optionHandler: PropTypes.func,
  responsive: PropTypes.bool,
};

export const confToFields = ({
  formStore,
  refs,
  fieldStores = null, // pass an array of fieldStores override which fields get rendered;
  responsive = false,
  optionHandler = () => {},
  fields = [], // []string
}) => {
  if (!formStore) return null;

  if (!fieldStores) {
    // eslint-disable-next-line no-param-reassign
    fieldStores = formStore.fieldConf
      .map(conf => {
        return formStore.fieldStores.get(conf.fieldName);
      })
      .filter(fieldStore => {
        if (fieldStore) return true;
        return false;
      });
  }

  if (fields.length !== 0) {
    // eslint-disable-next-line no-param-reassign
    fieldStores = fieldStores.filter(fStore =>
      fields.includes(fStore.fieldName),
    );
  }

  return fieldStores.map(fieldStore => {
    return (
      <FullFormField
        key={fieldStore.fieldName}
        fieldStore={fieldStore}
        refs={refs}
        optionHandler={optionHandler}
        responsive={responsive}
        visible={() => {
          if (fieldStore.dependsOn) {
            const keys = Object.keys(fieldStore.dependsOn);

            for (let i = 0; i < keys.length; i += 1) {
              const requiredValue = fieldStore.dependsOn[keys[i]];
              if (requiredValue !== formStore.fieldStores.get(keys[i])?.value) {
                return false;
              }
            }
          }

          return true;
        }}
      />
    );
  });
};

// This should probably not be in the framework as it is just one implmentation of a form
@withNamespaces()
@observer
export class Form extends React.Component {
  /* This is a terrible mess. Most of the parameters below should probably be form config
  and therefore included in the formStore TS */
  render() {
    const {
      className,
      formStore,
      formTitleColor,
      optionHandler,
      buttonHandler,
      formTitleCentered,
      noBorder,
      proTipMargin,
      warning,
      responsive,
      message,
      padding,
      isSideBarForm,
      maxResponsive,
    } = this.props;
    const refs = {};
    const {
      conf: {
        formTitle: formTitleConf,
        description,
        proTip,
        buttonText,
        buttonDataTestId,
        buttonTextPosting,
        styles = {},
      },
    } = formStore;
    const { formColumns = 1 } = styles;

    const formTitle =
      this.props.formTitle !== undefined ? this.props.formTitle : formTitleConf;

    // wrap formTitle, proTip, buttonText, buttonTextPosting in translation t() function?

    const fields = confToFields({ formStore, refs, optionHandler, responsive });
    const __html = message;

    return (
      <Main
        className={className}
        noBorder={noBorder}
        padding={padding}
        isSideBarForm={isSideBarForm}
      >
        <FormWrapper
          posting={formStore.posting}
          isSideBarForm={isSideBarForm}
          responsive
          maxResponsive={maxResponsive}
          onSubmit={e => {
            e.preventDefault();
            formStore.prepare();
            buttonHandler();
          }}
        >
          {formTitle && (
            <>
              <HeadingStyled
                size={isSideBarForm ? 'xmedium' : 'default'}
                centered={formTitleCentered}
                formTitleColor={formTitleColor}
                variant={isSideBarForm ? 'primary' : 'secondary'}
                isSideBarForm={isSideBarForm}
              >
                <Trans>{formTitle}</Trans>
              </HeadingStyled>
              {!isSideBarForm && <Divider spacing='default' />}
            </>
          )}
          {description && (
            <>
              <Paragraph>{description}</Paragraph>
              <Spacer size='default' />
            </>
          )}
          <FieldsWrapper formColumns={formColumns}>
            {formStore.conf.hasSocialSignInButtons && <SocialButtons />}
            {fields}
          </FieldsWrapper>
          {!!proTip && (
            <ProTip
              condition={!!proTip}
              form='true'
              margin={proTipMargin}
              size='small'
            >
              <strong>
                <Trans>Pro tip</Trans>:{' '}
              </strong>
              <Trans>{proTip}</Trans>
            </ProTip>
          )}
          {!!warning && (
            <Banner variant='warning'>
              <Trans>{warning}</Trans>
            </Banner>
          )}
          {!!message && <Message dangerouslySetInnerHTML={{ __html }} />}
          {buttonText && (
            <FormButtonWrapper isSideBarForm={isSideBarForm}>
              <FormActionButton
                ref={formButton => {
                  this._formbutton = formButton;
                }}
                formStore={formStore}
                data-test-id={buttonDataTestId}
                buttonText={buttonText}
                buttonTextPosting={buttonTextPosting}
                icon='arrow_right'
                iconPosition='right'
              />
            </FormButtonWrapper>
          )}
        </FormWrapper>
      </Main>
    );
  }
}

Form.defaultProps = {
  labelBold: false,
};

Form.propTypes = {
  className: PropTypes.string,
  conf: PropTypes.object,
  optionHandler: PropTypes.func,
  buttonHandler: PropTypes.func,
  formTitleCentered: PropTypes.bool,
  formTitleColor: PropTypes.string,
  formTitle: PropTypes.string,
  noBorder: PropTypes.bool,
  message: PropTypes.string,
  proTipMargin: PropTypes.string,
  formStore: PropTypes.object,
  spacing: PropTypes.string,
  warning: PropTypes.string,
  isSideBarForm: PropTypes.bool,
  padding: PropTypes.string,
  wizard: PropTypes.object,
  returnHandler: PropTypes.func,
  responsive: PropTypes.bool,
  maxResponsive: PropTypes.bool,
};
